import { Skeleton } from '@mui/material';
import React from 'react';
import { useParams } from 'react-router-dom';
import { useCatchUp } from '../../api/CatchUpsApi';
import { useDisbursement } from '../../api/DisbursementsApi';
import { usePayment } from '../../api/PaymentsApi';
import { usePolicyholder } from '../../api/PolicyholderApi';
import {
	UI_CASH_MANAGEMENT_FEATURE_ENABLED,
	useAuthContext,
} from '../../context/auth';
import { useConfigContext } from '../../context/config';
import {
	BREADCRUMB__INVOICE,
	BREADCRUMB__INVOICES,
} from '../../data/labelsAndMessages';
import { useRouteMatch } from '../../util/useRouteMatch';
import BreadcrumbsContainer, { Breadcrumb } from '../BreadcrumbsContainer';
import {
	CATCHUPS_BREADCRUMB,
	CATCHUPS_BREADCRUMBS__CATCHUP_DISPLAY_ID,
} from '../catchUps/data/constants';
import {
	DISBURSEMENTS_BREADCRUMB,
	DISBURSEMENTS_BREADCRUMB__EDIT,
	DISBURSEMENT_BREADCRUMB__DISPLAY_ID,
} from '../disbursements/data/constants';
import {
	PAYMENTS_BREADCRUMBS__EDIT,
	PAYMENTS_BREADCRUMBS__NEW,
	PAYMENTS_BREADCRUMBS__PAYMENTS,
	PAYMENTS_BREADCRUMBS__PAYMENT_DISPLAY_ID,
} from '../payments/data/constants';
import { getTemplateResult } from '../search/util';
import {
	PolicyholderRoutes,
	POLICYHOLDER_ROUTES,
} from './Sidebar/PolicyholderSidebar';

export const BREADCRUMBS_HEIGHT = 57;

const Breadcrumbs = () => {
	const authData = useAuthContext();
	const configData = useConfigContext();
	const authToken = authData?.auth?.authorizationToken;
	const params = useParams();
	const routeMatch = useRouteMatch(
		Object.keys(POLICYHOLDER_ROUTES).map(
			(key) => POLICYHOLDER_ROUTES[key as keyof PolicyholderRoutes],
		),
	);
	const policyholderLocator = params.policyholderId;
	const cashManagementEnabled =
		authData?.auth?.configProperties?.[UI_CASH_MANAGEMENT_FEATURE_ENABLED];

	const { data: payment, isLoading: isPaymentLoading } = usePayment(
		params.paymentLocator,
		authToken,
		cashManagementEnabled,
		authData?.hasPermission('payment.fetch'),
	);

	const { data: disbursement, isLoading: isDisbursementLoading } =
		useDisbursement(
			params.disbursementLocator,
			authToken,
			cashManagementEnabled,
			authData?.hasPermission('disbursement.fetch'),
		);
	const { data: catchUp, isLoading: isCatchUpLoading } = useCatchUp(
		params.catchUpLocator,
		authToken,
		cashManagementEnabled,
		authData?.hasPermission('catchUpInvoice.fetch'),
	);

	const {
		data: policyholderData,
		isLoading: isPolicyholderLoading,
		isError,
	} = usePolicyholder(policyholderLocator, authToken);

	if (
		isPolicyholderLoading ||
		(params.paymentLocator && isPaymentLoading) ||
		(params.disbursementLocator && isDisbursementLoading) ||
		(params.catchUpLocator && isCatchUpLoading)
	) {
		return <Skeleton variant='text' width={200} />;
	}

	if (isError) {
		return <BreadcrumbsContainer breadcrumbs={[]} />;
	}

	const policyholderCardDefinition =
		configData.policyholderCardConfig.workflows.find(
			(w: any) => w.message === 'policyHolderSearch.json',
		).value;
	const policyholderDisplayId =
		getTemplateResult(
			policyholderCardDefinition.subtitle,
			configData.ontologyData.items,
			policyholderData?.data?.entity.values,
		) || `Policyholder ${policyholderLocator}`;

	const breadcrumbs: Breadcrumb[] = [
		{
			href: `/policyholder/${policyholderLocator}`,
			text: policyholderDisplayId,
		},
	];

	// prepare invoices breadcrumbs
	if (
		[POLICYHOLDER_ROUTES.INVOICE, POLICYHOLDER_ROUTES.INVOICES].includes(
			routeMatch?.pattern?.path,
		)
	) {
		breadcrumbs.push({
			href: POLICYHOLDER_ROUTES.INVOICES.replace(
				':policyholderId',
				policyholderLocator,
			),
			text: BREADCRUMB__INVOICES(),
		});

		if (routeMatch?.pattern?.path === POLICYHOLDER_ROUTES.INVOICE) {
			breadcrumbs.push({
				href: POLICYHOLDER_ROUTES.INVOICE.replace(
					':policyholderId',
					policyholderLocator,
				).replace(':invoiceId', params.invoiceId),
				text: BREADCRUMB__INVOICE(params.invoiceId),
			});
		}
	}

	if (cashManagementEnabled) {
		// prepare payments breadcrumbs
		if (
			[
				POLICYHOLDER_ROUTES.PAYMENTS,
				POLICYHOLDER_ROUTES.EDIT_PAYMENT,
				POLICYHOLDER_ROUTES.NEW_PAYMENT,
				POLICYHOLDER_ROUTES.PAYMENT,
			].includes(routeMatch?.pattern?.path)
		) {
			breadcrumbs.push({
				href: POLICYHOLDER_ROUTES.PAYMENTS.replace(
					':policyholderId',
					policyholderLocator,
				),
				text: PAYMENTS_BREADCRUMBS__PAYMENTS,
			});
		}

		// prepare disbursement breadcrumbs
		if (
			[
				POLICYHOLDER_ROUTES.DISBURSEMENTS,
				POLICYHOLDER_ROUTES.DISBURSEMENT,
				POLICYHOLDER_ROUTES.EDIT_DISBURSEMENT,
			].includes(routeMatch?.pattern?.path)
		) {
			breadcrumbs.push({
				href: POLICYHOLDER_ROUTES.DISBURSEMENTS.replace(
					':policyholderId',
					policyholderLocator,
				),
				text: DISBURSEMENTS_BREADCRUMB,
			});
		}

		// prepare catch-up breadcrumbs
		if (
			[POLICYHOLDER_ROUTES.CATCHUPS, POLICYHOLDER_ROUTES.CATCHUP].includes(
				routeMatch?.pattern?.path,
			)
		) {
			breadcrumbs.push({
				href: POLICYHOLDER_ROUTES.CATCHUPS.replace(
					':policyholderId',
					policyholderLocator,
				),
				text: CATCHUPS_BREADCRUMB,
			});
		}

		switch (routeMatch?.pattern?.path) {
			case POLICYHOLDER_ROUTES.EDIT_PAYMENT: {
				breadcrumbs.push({
					href: POLICYHOLDER_ROUTES.PAYMENT.replace(
						':policyholderId',
						policyholderLocator,
					).replace(':paymentLocator', params.paymentLocator),
					text: PAYMENTS_BREADCRUMBS__PAYMENT_DISPLAY_ID(
						payment?.displayId || '',
					),
				});
				breadcrumbs.push({
					text: PAYMENTS_BREADCRUMBS__EDIT,
				});
				break;
			}
			case POLICYHOLDER_ROUTES.NEW_PAYMENT: {
				breadcrumbs.push({
					text: PAYMENTS_BREADCRUMBS__NEW,
				});
				break;
			}
			case POLICYHOLDER_ROUTES.PAYMENT: {
				breadcrumbs.push({
					text: PAYMENTS_BREADCRUMBS__PAYMENT_DISPLAY_ID(
						payment?.displayId || '',
					),
				});
				break;
			}
			case POLICYHOLDER_ROUTES.DISBURSEMENT: {
				breadcrumbs.push({
					text: DISBURSEMENT_BREADCRUMB__DISPLAY_ID(
						disbursement?.displayId || '',
					),
				});
				break;
			}
			case POLICYHOLDER_ROUTES.EDIT_DISBURSEMENT: {
				breadcrumbs.push({
					href: POLICYHOLDER_ROUTES.DISBURSEMENT.replace(
						':policyholderId',
						policyholderLocator,
					).replace(':disbursementLocator', params.disbursementLocator),
					text: DISBURSEMENT_BREADCRUMB__DISPLAY_ID(
						disbursement?.displayId || '',
					),
				});
				breadcrumbs.push({
					text: DISBURSEMENTS_BREADCRUMB__EDIT,
				});
				break;
			}
			case POLICYHOLDER_ROUTES.CATCHUP: {
				breadcrumbs.push({
					text: CATCHUPS_BREADCRUMBS__CATCHUP_DISPLAY_ID(
						catchUp?.displayId || '',
					),
				});
				break;
			}
		}
	}

	return <BreadcrumbsContainer breadcrumbs={breadcrumbs} />;
};

export default Breadcrumbs;
